import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../contexts/BranchContext';
import { useConfirm } from '../contexts/ConfirmContext';
import Toast from '../components/Toast';

const baht = (n) => (Number(n) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const when = (d) => (d ? new Date(d).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' }) : '-');

const STATUS_LABELS = {
  open: { text: 'กำลังนับ', color: '#f39c12' },
  closing: { text: 'กำลังปิด', color: '#f39c12' },
  closed: { text: 'ปิดแล้ว', color: '#27ae60' },
  cancelled: { text: 'ยกเลิก', color: '#95a5a6' }
};

export default function StockCount({ apiUrl }) {
  const navigate = useNavigate();
  const { effectiveBranchId, isAllBranches } = useBranch();
  const confirm = useConfirm();
  // เดินสต็อกปรับยอดคงเหลือจริงของสาขาเดียวเสมอ ห้ามเผลอทำตอนเลือก "รวมทุกสาขา"
  const blocked = isAllBranches || !effectiveBranchId;
  const [sessions, setSessions] = useState([]);
  const [openSession, setOpenSession] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม

  const token = () => localStorage.getItem('adminToken');

  useEffect(() => {
    if (blocked) {
      setLoading(false);
      return;
    }
    loadData();
  }, [blocked]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [listRes, openRes] = await Promise.all([
        fetch(`${apiUrl}/api/stock-counts?limit=30`, { headers: { 'Authorization': `Bearer ${token()}` } }),
        fetch(`${apiUrl}/api/stock-counts/open`, { headers: { 'Authorization': `Bearer ${token()}` } })
      ]);
      const list = await listRes.json();
      const open = await openRes.json();
      if (listRes.ok) setSessions(list.data || []);
      if (openRes.ok) setOpenSession(open.data || null);
    } catch (error) {
      console.error('Failed to load stock counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const startSession = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await fetch(`${apiUrl}/api/stock-counts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify({ scope: { type: 'all' } })
      });
      const result = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: 'เริ่มรอบนับแล้ว — ให้พนักงานกรอกยอดจากแอปพนักงาน' });
        loadData();
      } else {
        setMessage({ type: 'error', text: result.message || 'เริ่มรอบนับไม่สำเร็จ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด' });
    } finally {
      setBusy(false);
    }
  };

  const closeSession = async (id) => {
    if (!await confirm('ระบบจะปรับสต็อกตามส่วนต่างที่นับได้', { title: 'ปิดรอบนับนี้?', confirmText: 'ปิดรอบนับ' })) return;
    setBusy(true);
    try {
      const response = await fetch(`${apiUrl}/api/stock-counts/${id}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify({ confirm: true })
      });
      const result = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: `ปิดรอบแล้ว — ปรับ ${result.data.applied} รายการ` });
        loadData();
      } else {
        setMessage({ type: 'error', text: result.message || 'ปิดรอบไม่สำเร็จ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด' });
    } finally {
      setBusy(false);
    }
  };

  const cancelSession = async (id) => {
    if (!await confirm('ยอดที่กรอกไว้จะไม่ถูกนำไปปรับสต็อก', { title: 'ยกเลิกรอบนับนี้?', confirmText: 'ยกเลิกรอบนับ', danger: true })) return;
    setBusy(true);
    try {
      await fetch(`${apiUrl}/api/stock-counts/${id}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token()}` }
      });
      loadData();
    } finally {
      setBusy(false);
    }
  };

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#fff3cd', color: '#856404', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — เดินสต็อกต้องระบุสาขาเดียวเสมอ
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการทำรายการผิดสาขาโดยไม่ตั้งใจ
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div>กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>📋 เดินสต็อก</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {!openSession && (
            <button onClick={startSession} className="button" disabled={busy}>
              ➕ เริ่มรอบนับ
            </button>
          )}
          <button
            onClick={() => navigate('/stock')}
            className="button"
            style={{ background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)' }}
          >
            ← กลับหน้าสต็อก
          </button>
        </div>
      </div>

      <Toast message={message} onDismiss={() => setMessage(null)} />

      {/* Open session */}
      {openSession && (
        <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #f39c12' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>รอบนับที่กำลังเปิดอยู่</h3>
          <p style={{ color: '#bbb', fontSize: '0.9rem', marginBottom: '1rem' }}>
            เริ่มโดย {openSession.startedBy} เมื่อ {when(openSession.startedAt)} ·
            นับแล้ว {openSession.countedCount}/{(openSession.items || []).length} รายการ
          </p>
          <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1rem' }}>
            พนักงานกรอกยอดจากแอปพนักงานได้เลย — ปิดรอบเมื่อนับครบ
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button onClick={() => closeSession(openSession.id)} className="button" disabled={busy}>
              ✅ ปิดรอบนับ
            </button>
            <button
              onClick={() => cancelSession(openSession.id)}
              className="button"
              disabled={busy}
              style={{ background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)' }}
            >
              ยกเลิกรอบ
            </button>
          </div>
        </div>
      )}

      {/* History */}
      <div className="card">
        <h3 style={{ marginBottom: '1rem' }}>ประวัติรอบนับ</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="table-nowrap">
            <thead>
              <tr>
                <th>เริ่มเมื่อ</th>
                <th>ผู้เริ่ม</th>
                <th>สถานะ</th>
                <th style={{ textAlign: 'right' }}>รายการ</th>
                <th style={{ textAlign: 'right' }}>ขาด</th>
                <th style={{ textAlign: 'right' }}>เกิน</th>
                <th style={{ textAlign: 'right' }}>สุทธิ</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => {
                const badge = STATUS_LABELS[s.status] || { text: s.status, color: '#95a5a6' };
                return (
                  <tr key={s.id}>
                    <td>{when(s.startedAt)}</td>
                    <td>{s.startedBy}</td>
                    <td><span style={{ color: badge.color, fontWeight: 600 }}>{badge.text}</span></td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {s.summary?.lineCount ?? s.countedCount}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#e74c3c' }}>
                      {s.summary ? `${baht(s.summary.shortValue)} ฿` : '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#27ae60' }}>
                      {s.summary ? `+${baht(s.summary.overValue)} ฿` : '-'}
                    </td>
                    <td style={{
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 600,
                      color: (s.summary?.varianceValue ?? 0) < 0 ? '#e74c3c' : '#27ae60'
                    }}>
                      {s.summary ? `${baht(s.summary.varianceValue)} ฿` : '-'}
                    </td>
                    <td>
                      {s.status === 'closed' && (
                        <button
                          onClick={() => setSelected(selected?.id === s.id ? null : s)}
                          className="button"
                          style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
                        >
                          {selected?.id === s.id ? 'ซ่อน' : 'ดู'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {sessions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              ยังไม่เคยเดินสต็อก
            </div>
          )}
        </div>
      </div>

      {/* Drill-in */}
      {selected && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>
            รายละเอียดรอบ {when(selected.startedAt)}
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="table-nowrap">
              <thead>
                <tr>
                  <th>รายการ</th>
                  <th style={{ textAlign: 'right' }}>ระบบว่ามี</th>
                  <th style={{ textAlign: 'right' }}>นับได้</th>
                  <th style={{ textAlign: 'right' }}>ส่วนต่าง</th>
                  <th style={{ textAlign: 'right' }}>มูลค่า</th>
                  <th>ผู้นับ</th>
                </tr>
              </thead>
              <tbody>
                {(selected.lines || []).map(line => (
                  <tr key={line.menuItemId}>
                    <td style={{ fontWeight: 600 }}>
                      {Math.abs(line.movementSince || 0) > 0.0001 && (
                        <span title="มีการขายระหว่างนับ" style={{ marginRight: '0.3rem' }}>⚠️</span>
                      )}
                      {line.name}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {line.expectedAtEntry} {line.unit}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {line.countedQuantity} {line.unit}
                    </td>
                    <td style={{
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: line.delta < 0 ? '#e74c3c' : line.delta > 0 ? '#27ae60' : '#888'
                    }}>
                      {line.delta > 0 ? '+' : ''}{line.delta}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {baht(line.varianceValue)} ฿
                    </td>
                    <td>{line.countedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
